import { useState } from "react";
import { CommandBarButton, Icon, Stack, TextField, mergeStyles, TooltipHost, DefaultButton, FontWeights, ImageFit } from "@fluentui/react";
import { t } from "i18next";
import { ChatbotHelper } from "../../../Chatbot/ChatHelper";
import { useMediaQuery } from "react-responsive";

interface Props {
    onSend: (question: string, id?: string) => void;
    onStopGenerating: () => void;
    onClearChat: () => void;
    onSupportRequest: () => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
    conversationId?: string;
    showSupportIcon?: boolean;
}

export const QuestionInput:React.FC<Props> = (props) => {
    const [question, setQuestion] = useState<string>("");
    const isMobile = useMediaQuery({ maxWidth: 768 });
    
    //#region Styles

    const inputContainerStyles = mergeStyles([{
        height: "120px",
        width: "100%",
        position: "absolute",
        left: "0",
        right: "0",
        top: "0",
        bottom: 0,
        background: "#FFFFFF",
        boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
        borderRadius: "8px",
        fontFamily: "Nunito"
    }]);

    const questionInputSendButtonContainerStyles = mergeStyles([{
        position: "absolute",
        right: "10px",
        bottom: "5px",
        width: "50%"
    }]);

    const stopGeneratingIconStyles = mergeStyles([{
        width: "20%",
        cursor: "pointer",
        color: ChatbotHelper.getButtonBackground(),
        fontWeight: "bold",
        border: "none",
        background: "none",
        "&:hover": {
            color: ChatbotHelper.getButtonBackground(),
            background: "none",
        },
        "&:active": {
            color: ChatbotHelper.getButtonBackground(),
            background: "none",
        }
    }]);

    const questionInputSendButtonStyles = mergeStyles([{
        width: "20%",
        cursor: "pointer",
        color: ChatbotHelper.getButtonBackground(),
        fontWeight: "bold",
        border: "none",
        background: "none",
        "&:hover": {
            backgroundColor: "trasparent"
        },
        "&:active": {
            backgroundColor: "trasparent"
        }
    }]);

    const questionInputButtonsContainerStyles = mergeStyles([{
        height: "35%",
        width: "100%",
        position: "relative"
    }])

    const questionInputUtilityButtonsContainerStyles = mergeStyles([{
        position: "absolute",
        left: "10px",
        bottom: "5px",
        width: "50%",
    }]);

    const questionInputClearButtonStyles = mergeStyles([{
        marginRight: "1dvw",
        color: "#FFFFFF",
        background: ChatbotHelper.getButtonBackground(),
        fontFamily: "Nunito",
        fontWeight: 600,
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        "&:hover": {
            color: "#FFFFFF",
            background: ChatbotHelper.getButtonHoveredBackground()
        },
        "&:active": {
            color: "#FFFFFF",
            background: ChatbotHelper.getButtonPressedBackground()
        }
    }]);

    const questionInputHelpButtonStyles = mergeStyles([{
        background: ChatbotHelper.getButtonBackground(),
        color: "#FFFFFF",
        fontFamily: "Nunito",
        fontWeight: 600,
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        "&:hover": {
            color: "#FFFFFF",
            background: ChatbotHelper.getButtonHoveredBackground()
        },
        "&:active": {
            color: "#FFFFFF",
            background: ChatbotHelper.getButtonPressedBackground()
        }
    }]);

    //#endregion


    const sendQuestion = () => {
        if (props.disabled || !question.trim()) {
            return;
        }

        if(props.conversationId){
            props.onSend(question, props.conversationId);
        }else{
            props.onSend(question);
        }

        if (props.clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setQuestion(newValue || "");
    };

    const sendQuestionDisabled = props.disabled || !question.trim();


    return (
        <Stack
            className={inputContainerStyles}
            verticalFill
            verticalAlign="space-between"
        >
            <Stack
                styles={{
                    root: {
                        height: "65%"
                    }
                }}
            >
                <TextField 
                    placeholder={props.placeholder}
                    multiline
                    multiple
                    resizable={false}
                    borderless
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    styles={{
                        root: {
                            height: "100% !important",
                            fontFamily: "Nunito"
                        },
                        field: {
                            height: "inherit !important",
                            fontFamily: "Nunito"
                        },
                        fieldGroup: {
                            height: "100% !important",
                            border: "none",
                            borderTopLeftRadius: "8px",
                            borderTopRightRadius: "8px",
                        },
                        wrapper: {
                            height: "100% !important"
                        }
                    }}
                />
            </Stack>
            <Stack
                className={questionInputButtonsContainerStyles}
            >
                <Stack
                    className={questionInputUtilityButtonsContainerStyles}
                    horizontal
                >
                    <DefaultButton
                        className={questionInputClearButtonStyles}
                        iconProps={{ iconName: 'RecycleBin' }}
                        onClick={()=>{props.onClearChat()}}
                    >
                        { !isMobile && t("chatbot:clearChat") }
                    </DefaultButton>
                    {
                        props.showSupportIcon &&
                        <DefaultButton
                            className={questionInputHelpButtonStyles}
                            iconProps={{ iconName: 'Help' }}
                            onClick={()=>{props.onSupportRequest()}}
                        >
                            { !isMobile && t("chatbot:supportRequest") }
                        </DefaultButton>
                    }
                </Stack>
                <Stack
                    className={questionInputSendButtonContainerStyles}
                    horizontal
                    horizontalAlign="end"
                >
                    {
                        sendQuestionDisabled ?
                            props.disabled ?
                                <DefaultButton
                                    className={stopGeneratingIconStyles}
                                    iconProps={{
                                        iconName: 'CircleStopSolid'
                                    }}
                                    styles={{
                                        icon: {
                                            height: "1.4rem",
                                            fontSize: "1.4rem"
                                        }
                                    }}
                                    onClick={() => {props.onStopGenerating()}}
                                />
                            :
                                <DefaultButton
                                    className={questionInputSendButtonStyles}
                                    iconProps={{
                                        iconName: 'Send'
                                    }}
                                    styles={{
                                        icon: {
                                            height: "1.4rem",
                                            fontSize: "1.4rem"
                                        }
                                    }}
                                    disabled
                                />
                        :
                            <DefaultButton
                                className={questionInputSendButtonStyles}
                                iconProps={{
                                    imageProps: {
                                        src: ChatbotHelper.getSendIcon(),
                                        width: "inherit",
                                        height: "inherit",
                                        alt: "sendImage"
                                    }
                                }}
                                styles={{
                                    icon: {
                                        height: "1.6rem",
                                    }
                                }}
                                onClick={() => {sendQuestion()}}
                            />
                    }
                </Stack>
            </Stack>
        </Stack>
    );
};